import { Component, Input, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';
import { ApiService } from '../../../services/api.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent {
  @Input() sidebarOpen: boolean = true;
  
  showUserMenu = false;
  showResetPasswordModal = false;
  resetPasswordForm = {
    newPassword: '',
    confirmPassword: ''
  };
  showNewPassword = false;
  showConfirmPassword = false;
  resetPasswordError = '';
  resetPasswordSuccess = '';
  resetPasswordLoading = false;
  newPasswordError = '';
  confirmPasswordError = '';
  resetPasswordNotification = '';
  resetPasswordNotificationType: 'success' | 'error' | null = null;
  
  appName = environment.appName;
  appSubtitle = environment.appSubtitle;

  constructor(
    public authService: AuthService,
    private apiService: ApiService,
    private elementRef: ElementRef
  ) {}

  toggleUserMenu(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.showUserMenu = !this.showUserMenu;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.showUserMenu) {
      return;
    }
    
    const target = event.target as HTMLElement;
    const container = this.elementRef.nativeElement.querySelector('.user-menu-container');
    
    if (container && !container.contains(target)) {
      this.showUserMenu = false;
    }
  }

  closeUserMenu() {
    this.showUserMenu = false;
  }

  logout() {
    this.closeUserMenu();
    this.authService.logout();
  }

  openResetPasswordModal() {
    this.closeUserMenu();
    this.showResetPasswordModal = true;
    this.resetPasswordForm = {
      newPassword: '',
      confirmPassword: ''
    };
    this.showNewPassword = false;
    this.showConfirmPassword = false;
    this.resetPasswordError = '';
    this.resetPasswordSuccess = '';
    this.newPasswordError = '';
    this.confirmPasswordError = '';
    this.resetPasswordNotification = '';
    this.resetPasswordNotificationType = null;
  }

  closeResetPasswordModal() {
    this.showResetPasswordModal = false;
    this.resetPasswordForm = {
      newPassword: '',
      confirmPassword: ''
    };
    this.showNewPassword = false;
    this.showConfirmPassword = false;
    this.resetPasswordError = '';
    this.resetPasswordSuccess = '';
    this.newPasswordError = '';
    this.confirmPasswordError = '';
    this.resetPasswordNotification = '';
    this.resetPasswordNotificationType = null;
  }

  toggleNewPasswordVisibility() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  resetPassword() {
    // Clear all error messages
    this.resetPasswordError = '';
    this.resetPasswordSuccess = '';
    this.newPasswordError = '';
    this.confirmPasswordError = '';

    // Validate New Password
    if (!this.resetPasswordForm.newPassword) {
      this.newPasswordError = 'Password is required';
      return;
    }

    if (this.resetPasswordForm.newPassword.length < 6) {
      this.newPasswordError = 'Password must be at least 6 characters long';
      return;
    }

    // Validate Confirm Password
    if (!this.resetPasswordForm.confirmPassword) {
      this.confirmPasswordError = 'Please confirm your password';
      return;
    }

    if (this.resetPasswordForm.newPassword !== this.resetPasswordForm.confirmPassword) {
      this.confirmPasswordError = 'Passwords do not match';
      return;
    }

    this.resetPasswordLoading = true;

    this.apiService.resetPassword(
      this.resetPasswordForm.newPassword
    ).subscribe({
      next: (response) => {
        // Clear all errors
        this.resetPasswordError = '';
        this.newPasswordError = '';
        this.confirmPasswordError = '';
        this.resetPasswordSuccess = '';
        
        this.resetPasswordLoading = false;
        
        // Show success notification
        this.resetPasswordNotification = response.message || 'Password reset successfully!';
        this.resetPasswordNotificationType = 'success';
        
        // Close modal after showing success
        setTimeout(() => {
          this.closeResetPasswordModal();
        }, 500);
        
        // Clear notification after 3 seconds
        setTimeout(() => {
          this.resetPasswordNotification = '';
          this.resetPasswordNotificationType = null;
        }, 3000);
      },
      error: (err) => {
        this.resetPasswordLoading = false;
        
        // Clear success notification
        this.resetPasswordNotification = '';
        this.resetPasswordNotificationType = null;
        
        // Determine error message
        let errorMessage = '';
        if (err.status === 400 && err.error?.message) {
          const errorMsg = err.error.message.toLowerCase();
          if (errorMsg.includes('password') && (errorMsg.includes('required') || errorMsg.includes('length') || errorMsg.includes('6'))) {
            // Field-specific validation error - show below field
            this.newPasswordError = err.error.message;
            return; // Don't show notification for field-specific errors
          } else {
            errorMessage = err.error.message;
          }
        } else if (err.status === 0) {
          errorMessage = 'Cannot connect to server. Please make sure the backend API is running on http://localhost:4000';
        } else if (err.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (err.status === 403) {
          errorMessage = 'Access denied.';
        } else if (err.status === 500) {
          errorMessage = err.error?.message || 'Server error. Please try again later.';
        } else {
          errorMessage = err.error?.message || 'Error resetting password. Please try again.';
        }
        
        // Show error notification (modal stays open)
        this.resetPasswordNotification = errorMessage;
        this.resetPasswordNotificationType = 'error';
        this.resetPasswordError = errorMessage;
        
        // Clear notification after 3 seconds
        setTimeout(() => {
          this.resetPasswordNotification = '';
          this.resetPasswordNotificationType = null;
        }, 3000);
      }
    });
  }

  getUserName(): string {
    const user = this.authService.getCurrentUser();
    return user?.name || 'Admin';
  }

  getUserEmail(): string {
    const user = this.authService.getCurrentUser();
    return user?.email || 'ad@aeroplastltd.com';
  }

  getUserType(): string {
    const user = this.authService.getCurrentUser();
    if (user && user.userType) {
      // Format userType for display
      if (user.userType === 'ProcurementManager') {
        return 'Procurement Manager';
      }
      return user.userType;
    }
    return 'Admin';
  }
}

